import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, Download, Grid, List, 
  File, FileText, Image, Video, Music, Eye, EyeOff, X,
  ChevronRight, ChevronDown, Home, Folder, CheckSquare, Square, Package
} from 'lucide-react';
import { supabase } from '../config/supabase';
import { KnowledgeFile, PermissionLevel, Category } from '../types';
import { PERMISSION_LABELS, PERMISSION_COLORS } from '../utils/permissions';
import { cn } from '../utils/cn';
import PublicLayout from '../components/layout/PublicLayout';
import MobileOrderQuery from '../components/layout/MobileOrderQuery';
import { ToastProvider, useToast } from '../components/shared/ToastContainer';
import FilePreviewModal from '../components/shared/FilePreviewModal';
import JSZip from 'jszip';
import { useDelayedLoading } from '../hooks/useDelayedLoading';
import { SkeletonGrid, SkeletonList } from '../components/shared/Skeleton';
import Loader from '../components/shared/Progressbar';
import { PortalFileCard, PortalSearchBar, PortalToolbar } from '../components/portal';
import { useI18n } from '../i18n';
import { useTenant } from '../contexts/TenantContext';
import { withTimeout } from '../utils/with-timeout';

const PORTAL_AUTH_TIMEOUT_MS = 4000;
const PORTAL_AUTH_RETRY_DELAYS_MS = [1_000, 3_000, 8_000];

interface PublicUser {
  id: string;
  email: string;
  role: PermissionLevel;
  name?: string;
  avatar_url?: string;
}

interface PortalCategory extends Category {
  file_count?: number;
  children?: PortalCategory[];
}

const PublicPortalPageContent: React.FC = () => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const { tenant } = useTenant(); // 获取当前租户 (cn/intl)
  const [user, setUser] = useState<PublicUser | null>(null);
  const [files, setFiles] = useState<KnowledgeFile[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<KnowledgeFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const authRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<(KnowledgeFile & { url?: string }) | null>(null);
  
  // 使用延迟加载 hook 实现 500ms 阈值原则
  // 快速加载时不显示任何加载状态，避免闪烁
  const { showLoading } = useDelayedLoading(isLoading);
  
  // 批量下载状态
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isDownloadingBatch, setIsDownloadingBatch] = useState(false);
  const [batchMode, setBatchMode] = useState(false); // 批量操作模式开关
  
  // 筛选状态
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoryPath, setCategoryPath] = useState<PortalCategory[]>([]);
  const [availableCategories, setAvailableCategories] = useState<PortalCategory[]>([]);
  
  // 页面切换状态
  const [currentView, setCurrentView] = useState<'files' | 'profile' | 'order'>('files');
  
  // 侧边栏刷新触发器
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState<number>(0);
  
  // 个人资料编辑状态
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: ''
  });
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  // 订单查询状态
  
  // 智能搜索状态
  const [isSmartSearching, setIsSmartSearching] = useState(false);
  const [searchResultType, setSearchResultType] = useState<'file' | 'order' | 'folder' | null>(null);
  const [searchSuggestions, setSearchSuggestions] = useState<Array<{
    type: 'file' | 'order' | 'folder';
    text: string;
    categoryId?: string;
    orderNumber?: string;
    description?: string;
  }>> ([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // 🆕 文件夹内容缓存（避免重复加载）
  // key: categoryId, value: { files, totalCount, totalSize, timestamp }
  const categoryFilesCacheRef = useRef<Map<string, {
    files: KnowledgeFile[];
    totalCount: number;
    totalSize: number;
    timestamp: number;
  }>>(new Map());
  
  // 缓存有效期：5分钟
  const CACHE_TTL = 5 * 60 * 1000;

  useEffect(() => {
    void checkAuthentication();
    return () => {
      if (authRetryTimerRef.current) clearTimeout(authRetryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchAccessibleFiles();
      // 初始化个人资料表单
      setProfileForm({
        name: user.name || ''
      });
    }
  }, [isAuthenticated, user]);

  // 防抖生成搜索建议
  useEffect(() => {
    const delayedSuggestion = setTimeout(() => {
      if (searchQuery && !searchResultType) {
        generateSearchSuggestions(searchQuery);
      }
    }, 400); // 稍微延迟生成建议
    
    return () => clearTimeout(delayedSuggestion);
  }, [searchQuery, availableCategories, isAuthenticated]);

  // 本地过滤（仅处理类型筛选和标签筛选，不自动触发搜索）
  useEffect(() => {
    // 只有在没有搜索词时才执行本地过滤
    // 有搜索词时需要用户按回车或点击搜索按钮手动触发
    if (!searchQuery.trim()) {
      filterFiles();
    }
  }, [filterType, selectedTags, selectedCategory]);

  useEffect(() => {
    // 获取标签和分类数据
    fetchTagsAndCategories();
  }, [isAuthenticated, user, tenant]); // 添加 tenant 依赖，切换租户时重新获取

  const fetchTagsAndCategories = async () => {
    try {
      let allAccessibleCategories: PortalCategory[] = [];

      
      if (isAuthenticated && user) {
        // 1. 获取当前租户的公共文件夹（排除受限公开）
        const { data: publicCats, error: publicError } = await supabase
          .from('categories')
          .select('id, name, parent_id, created_at, is_public, visibility_type, file_count:file_categories(count)')
          .eq('tenant', tenant) // 按租户过滤
          .or('is_public.eq.true,visibility_type.eq.public')
          .neq('visibility_type', 'restricted_public')
          .order('name');

          if (publicError) console.error('Public cats error:', publicError);

        // 2. 获取当前租户中用户有权限的私有文件夹
        const { data: privateCats } = await supabase
          .from('categories')
          .select(`
            id, 
            name, 
            parent_id, 
            created_at, 
            is_public,
            visibility_type,
            file_count:file_categories(count),
            category_permissions!inner(user_id)
          `)
          .eq('tenant', tenant) // 按租户过滤
          .or('is_public.eq.false,visibility_type.eq.private,visibility_type.eq.restricted_public')
          .eq('category_permissions.user_id', user.id)
          .order('name');

        // 3. 合并结果，去重
        const normalizeCategory = (cat: any): PortalCategory => ({
          ...cat,
          file_count: cat.file_count?.[0]?.count || 0
        });

        const allCats = [...(publicCats || []).map(normalizeCategory), ...(privateCats || []).map(normalizeCategory)];
        allAccessibleCategories = allCats.filter((cat, index, self) => 
          index === self.findIndex(c => c.id === cat.id)
        );
      } else {
        // 未登录用户：仅获取当前租户的完全公共文件夹，排除受限公开
        const { data: publicCats } = await supabase
          .from('categories')
          .select('id, name, parent_id, created_at, is_public, visibility_type, file_count:file_categories(count)')
          .eq('tenant', tenant) // 按租户过滤
          .or('is_public.eq.true,visibility_type.eq.public')
          .neq('visibility_type', 'restricted_public')
          .order('name');
          
        allAccessibleCategories = (publicCats || []).map((cat: any) => ({
          ...cat,
          file_count: cat.file_count?.[0]?.count || 0
        }));
      }

      if (allAccessibleCategories.length > 0) {
        // 构建分类树
        const buildCategoryTree = (categories: PortalCategory[], parentId: string | null = null): PortalCategory[] => {
          return categories
            .filter(cat => cat.parent_id === parentId)
            .map(cat => ({
              ...cat,
              children: buildCategoryTree(categories, cat.id)
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
        };

        const categoryTree = buildCategoryTree(allAccessibleCategories);
        setAvailableCategories(categoryTree);
      } else {
        setAvailableCategories([]);
      }
    } catch (error) {
      console.error('获取标签和分类失败:', error);
    }
  };

  const scheduleAuthenticationRetry = (attempt: number) => {
    if (attempt >= PORTAL_AUTH_RETRY_DELAYS_MS.length) return;
    if (authRetryTimerRef.current) clearTimeout(authRetryTimerRef.current);
    authRetryTimerRef.current = setTimeout(() => {
      authRetryTimerRef.current = null;
      void checkAuthentication(attempt + 1);
    }, PORTAL_AUTH_RETRY_DELAYS_MS[attempt]);
  };

  const checkAuthentication = async (attempt = 0) => {
    try {
      const { data: { session } } = await withTimeout(
        supabase.auth.getSession(),
        PORTAL_AUTH_TIMEOUT_MS,
        'Portal session check',
      );

      if (!session) {
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      if (session) {
        const { data: profile, error: profileError } = await withTimeout(
          supabase
            .from('v_fs_users')
            .select('id, email, role, name, avatar_url')
            .eq('id', session.user.id)
            .single(),
          PORTAL_AUTH_TIMEOUT_MS,
          'Portal profile lookup',
        );

        if (profileError) {
          throw profileError;
        }
        
        if (profile) {
          if (authRetryTimerRef.current) {
            clearTimeout(authRetryTimerRef.current);
            authRetryTimerRef.current = null;
          }
          setUser(profile as PublicUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    } catch (error) {
      console.warn('认证检查超时或失败，按游客模式继续:', error);
      setUser(null);
      setIsAuthenticated(false);
      scheduleAuthenticationRetry(attempt);
    } finally {
      setIsLoading(false);
    }
  };

  // 分页参数 - 只加载前 10 个文件，其余让用户搜索
  // 分页参数 - 分页策略：首次进入加载10个，点击加载更多加载50个
  const INITIAL_PAGE_SIZE = 10;
  const LOAD_MORE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(0);
  const [totalFilesCount, setTotalFilesCount] = useState(0);
  const [totalFilesSize, setTotalFilesSize] = useState(0);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 获取用户可访问的文件夹 ID 列表
  const getAccessibleCategoryIds = async (): Promise<string[]> => {
    if (isAuthenticated && user) {
      // 已登录用户
      // 1. 获取公共文件夹ID（排除受限公开）
      const { data: publicCats } = await supabase
        .from('categories')
        .select('id')
        .eq('tenant', tenant)
        .or('is_public.eq.true,visibility_type.eq.public')
        .neq('visibility_type', 'restricted_public');
      const publicCategoryIds = publicCats?.map(c => c.id) || [];

      // 2. 获取用户有权限的私有文件夹ID
      const { data: privateCats } = await supabase
        .from('category_permissions')
        .select('category_id')
        .eq('user_id', user.id);
      const privateCategoryIds = privateCats?.map(cp => cp.category_id) || [];

      return [...new Set([...publicCategoryIds, ...privateCategoryIds])];
    } else {
      // 未登录用户：仅获取公共文件夹
      const { data: publicCats } = await supabase
        .from('categories')
        .select('id')
        .eq('tenant', tenant)
        .or('is_public.eq.true,visibility_type.eq.public')
        .neq('visibility_type', 'restricted_public');
      return publicCats?.map(c => c.id) || [];
    }
  };

  // 加载文件夹中的文件（分页，支持缓存）
  const fetchCategoryFilesForPortal = async (
    categoryId: string, 
    reset = false, 
    limit = LOAD_MORE_SIZE,
    forceRefresh = false  // 🆕 强制刷新，忽略缓存
  ) => {
    if (isLoadingMore && !reset) return;
    
    // 🆕 检查缓存（仅在首次加载且非强制刷新时）
    if (reset && !forceRefresh) {
      const cached = categoryFilesCacheRef.current.get(categoryId);
      if (cached && (Date.now() - cached.timestamp < CACHE_TTL)) {
        // 缓存有效，直接使用
        console.log(`[Cache Hit] 使用缓存数据: ${categoryId}, 文件数: ${cached.files.length}`);
        setFiles(cached.files);
        setFilteredFiles(cached.files);
        setTotalFilesCount(cached.totalCount);
        setTotalFilesSize(cached.totalSize);
        setHasMoreFiles(cached.files.length < cached.totalCount);
        return;
      }
    }
    
    setIsLoadingMore(true);
    
    try {
      const offset = reset ? 0 : files.length;
      // 如果是重置（首次加载），使用初始页大小；否则使用传入的限制（默认50）
      const effectiveLimit = reset ? INITIAL_PAGE_SIZE : limit;
      
      // 1. 获取文件总数
      const { count: totalCount } = await supabase
        .from('file_categories')
        .select('*', { count: 'exact', head: true })
        .eq('category_id', categoryId);
      
      const actualTotal = totalCount || 0;
      setTotalFilesCount(actualTotal);
      
      // 2. 获取文件总大小（首次加载）
      let totalSize = 0;
      if (reset) {
        const { data: sizeData } = await supabase.rpc('get_category_total_size', { p_category_id: categoryId });
        totalSize = sizeData || 0;
        setTotalFilesSize(totalSize);
      }
      
      // 3. 分页查询文件
      const { data: fileCategories, error } = await supabase
        .from('file_categories')
        .select(`
          file:files(
            *,
            file_tags!left(tags(*)),
            file_categories!left(categories(id, name, is_public, visibility_type))
          )
        `)
        .eq('category_id', categoryId)
        .order('created_at', { foreignTable: 'files', ascending: false })
        .range(offset, offset + effectiveLimit - 1);
      
      if (error) throw error;
      
      // 4. 转换数据格式
      const newFiles = (fileCategories || [])
        .map((fc: any) => fc.file)
        .filter(Boolean)
        .map((file: any) => ({
          ...file,
          tags: file.file_tags?.map((ft: any) => ft.tags) || [],
          categories: file.file_categories?.map((fc: any) => fc.categories) || [],
          createdAt: file.created_at,
          updatedAt: file.updated_at,
          createdBy: file.created_by,
          updatedBy: file.updated_by,
          path: file.storage_key,
        }));
      
      const allFiles = reset ? newFiles : [...files, ...newFiles];
      const hasMore = allFiles.length < actualTotal;
      
      setFiles(allFiles);
      setFilteredFiles(allFiles); // 同步更新，避免等待 useEffect 防抖导致空状态闪烁
      setHasMoreFiles(hasMore);
      
      // 🆕 更新缓存（仅在首次加载时）
      if (reset) {
        categoryFilesCacheRef.current.set(categoryId, {
          files: allFiles,
          totalCount: actualTotal,
          totalSize: totalSize,
          timestamp: Date.now()
        });
        console.log(`[Cache Set] 缓存数据: ${categoryId}, 文件数: ${allFiles.length}`);
      }
      
    } catch (error) {
      console.error('获取文件夹文件失败:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };
  
  // 🆕 清除文件夹缓存
  const clearCategoryCache = (categoryId?: string) => {
    if (categoryId) {
      categoryFilesCacheRef.current.delete(categoryId);
      console.log(`[Cache Clear] 清除缓存: ${categoryId}`);
    } else {
      categoryFilesCacheRef.current.clear();
      console.log('[Cache Clear] 清除所有缓存');
    }
  };

  // 搜索文件（直接数据库查询）
  const searchFilesInDatabase = async (query: string) => {
    if (!query.trim()) {
      // 如果有选中的文件夹，重新加载该文件夹的文件
      if (selectedCategory) {
        fetchCategoryFilesForPortal(selectedCategory, true);
      } else {
        setFiles([]);
      }
      return;
    }
    
    setIsSearching(true);
    
    try {
      // 🔧 修复：递归获取文件夹及其所有子文件夹的 ID
      const getAllCategoryIds = (categories: PortalCategory[], targetId?: string): string[] => {
        const ids: string[] = [];
        for (const cat of categories) {
          // 如果指定了目标 ID，先找到目标文件夹
          if (targetId) {
            if (cat.id === targetId) {
              // 找到目标，获取它及其所有子文件夹
              ids.push(cat.id);
              if (cat.children && cat.children.length > 0) {
                ids.push(...getAllCategoryIds(cat.children));
              }
              return ids;
            }
            // 递归在子文件夹中查找
            if (cat.children && cat.children.length > 0) {
              const childIds = getAllCategoryIds(cat.children, targetId);
              if (childIds.length > 0) {
                return childIds;
              }
            }
          } else {
            // 没有目标 ID，获取所有文件夹
            ids.push(cat.id);
            if (cat.children && cat.children.length > 0) {
              ids.push(...getAllCategoryIds(cat.children));
            }
          }
        }
        return ids;
      };
      
      // 如果有选中的文件夹，在该文件夹及其子文件夹中搜索
      let categoryIdsToSearch: string[] = [];
      if (selectedCategory) {
        // 🔧 关键修复：获取当前文件夹及其所有子文件夹的 ID
        categoryIdsToSearch = getAllCategoryIds(availableCategories, selectedCategory);
        console.log('[Search] 在文件夹及子文件夹中搜索:', categoryIdsToSearch);
      } else {
        // 没有选中文件夹时，在所有可访问的文件夹中搜索
        categoryIdsToSearch = getAllCategoryIds(availableCategories);
        console.log('[Search] 全局搜索，可访问的文件夹 IDs:', categoryIdsToSearch.length);
      }
      
      if (categoryIdsToSearch.length === 0) {
        // 没有可访问的文件夹
        setFiles([]);
        setFilteredFiles([]);
        setTotalFilesCount(0);
        setHasMoreFiles(false);
        return;
      }
      
      // 使用 in 查询所有相关文件夹
      const { data: fileCategories, error } = await supabase
        .from('file_categories')
        .select(`
          category_id,
          file:files!inner(
            *,
            file_tags!left(tags(*)),
            file_categories!left(categories(id, name, is_public, visibility_type))
          )
        `)
        .in('category_id', categoryIdsToSearch)
        .ilike('files.name', `%${query}%`)
        .limit(20);
      
      if (error) throw error;
      
      // 去重（同一个文件可能在多个文件夹中）
      const uniqueFiles = new Map<string, any>();
      (fileCategories || []).forEach((fc: any) => {
        const file = fc.file;
        if (file && !uniqueFiles.has(file.id)) {
          uniqueFiles.set(file.id, {
            ...file,
            // 记录文件所属的第一个文件夹，方便跳转
            _searchCategoryId: fc.category_id,
            tags: file.file_tags?.map((ft: any) => ft.tags) || [],
            categories: file.file_categories?.map((fc: any) => fc.categories) || [],
            createdAt: file.created_at,
            updatedAt: file.updated_at,
            path: file.storage_key,
          });
        }
      });
      
      const filteredResults = Array.from(uniqueFiles.values());
      
      setFiles(filteredResults);
      setFilteredFiles(filteredResults);
      setTotalFilesCount(filteredResults.length);
      setHasMoreFiles(false);
      
      // 🆕 额外输出搜索结果用于调试
      console.log('[Search] 搜索结果:', filteredResults.length, '个文件');
      
    } catch (error) {
      console.error('搜索文件失败:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // ✅ 已删除 fetchAccessibleFiles - 不再一次性加载所有文件
  // 现在的策略：只有用户点击文件夹时才加载该文件夹的文件（前10个）
  const fetchAccessibleFiles = async () => {
    // 初始化时不加载文件，等用户选择文件夹
    setFiles([]);
    setIsLoading(false);
  };

  const getPermissionValue = (role: PermissionLevel): number => {
    const values: Record<PermissionLevel, number> = { 
      basic: 1, 
      intermediate: 2, 
      advanced: 3, 
      admin: 4,
      uploader: 2 // 上传管理员权限等同于intermediate
    };
    return values[role] || 1;
  };

  // 获取用户可访问的权限级别列表（用于数据库查询）
  const getAccessiblePermissionLevels = (role: PermissionLevel, isOrderFolder = false): string[] => {
    const userLevel = getPermissionValue(role);
    const levels: string[] = [];
    
    // 对于通过订单查询获得的受限公开文件夹，确保用户至少有intermediate权限
    const effectiveLevel = isOrderFolder ? Math.max(userLevel, 2) : userLevel;
    
    if (effectiveLevel >= 1) levels.push('basic');
    if (effectiveLevel >= 2) levels.push('intermediate'); 
    if (effectiveLevel >= 3) levels.push('advanced');
    if (effectiveLevel >= 4) levels.push('admin');
    
    return levels;
  };

  const handleTagToggle = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleCategorySelect = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
    
    // 确保从个人资料或订单查询视图切回文件视图
    if (currentView !== 'files') {
      setCurrentView('files');
    }
    
    if (categoryId) {
      const findCategoryPath = (categories: PortalCategory[], targetId: string): PortalCategory[] => {
        for (const category of categories) {
          if (category.id === targetId) {
            return [category];
          }
          if (category.children) {
            const path = findCategoryPath(category.children, targetId);
            if (path.length > 0) {
              return [category, ...path];
            }
          }
        }
        return [];
      };
      
      const path = findCategoryPath(availableCategories, categoryId);
      setCategoryPath(path);
      
      // 🆕 加载该文件夹的文件
      fetchCategoryFilesForPortal(categoryId, true);
    } else {
      // 🔧 修复：回到"全部文件"时，清除所有搜索相关状态
      setCategoryPath([]);
      setFiles([]);
      setFilteredFiles([]);
      setTotalFilesCount(0);
      setTotalFilesSize(0);
      // 清除搜索状态
      setSearchQuery('');
      setSearchResultType(null);
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setFilterType('all');
      setSelectedTags([]);
    }
  };

  const resetAllFilters = () => {
    setSearchQuery('');
    setFilterType('all');
    setSelectedTags([]);
    setSelectedCategory(null);
    setCategoryPath([]);
    
    // 确保切回文件视图
    if (currentView !== 'files') {
      setCurrentView('files');
    }
    // 清除搜索相关状态
    setSearchResultType(null);
    setSearchSuggestions([]);
    setShowSuggestions(false);
    
    // 🔧 关键修复：清除文件列表，让页面回到初始的文件夹列表视图
    setFiles([]);
    setFilteredFiles([]);
    setTotalFilesCount(0);
    setTotalFilesSize(0);
    
    // 清除缓存
    clearCategoryCache();
  };

  // 处理搜索输入变化（仅更新输入值和建议，不触发搜索）
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim()) {
      // 有输入时显示建议框
      setShowSuggestions(true);
    } else {
      // 清空时隐藏建议框并重置状态
      setSearchResultType(null);
      setSearchSuggestions([]);
      setShowSuggestions(false);
      // 如果当前选中了文件夹，重新加载该文件夹的文件
      if (selectedCategory) {
        fetchCategoryFilesForPortal(selectedCategory, true);
      }
    }
  };

  // 手动触发搜索（按回车或点击搜索按钮）
  const handleSearchSubmit = () => {
    if (!searchQuery.trim()) return;
    
    setShowSuggestions(false);
    setSearchResultType('file');
    searchFilesInDatabase(searchQuery);
  };

  // 处理搜索框键盘事件
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // 订单查询处理函数 - 优化版本，使用数据库存储过程
  const handleOrderQuery = async (orderNumber: string) => {
    if (!user || !orderNumber.trim()) {
      toast.addToast('请先登录并输入有效的订单号', 'warning');
      return;
    }

    try {
      // 🚀 使用优化后的 RPC 调用，将原来 10+ 次请求合并为 1 次
      const { data: result, error: rpcError } = await supabase
        .rpc('query_order_with_permissions', {
          p_order_number: orderNumber.trim(),
          p_user_id: user.id
        });

      if (rpcError) {
        console.error('订单查询RPC错误:', rpcError);
        throw rpcError;
      }

      // 处理 RPC 返回结果
      if (!result.success) {
        const errorMessages: Record<string, string> = {
          'ORDER_NOT_FOUND': '未找到该订单号或订单已失效',
          'ORDER_EXPIRED': '该订单已过期，无法查询',
          'INTERNAL_ERROR': result.message || '订单查询失败'
        };
        toast.addToast(errorMessages[result.error] || '订单查询失败', 'error');
        return;
      }

      const { 
        category_id: categoryId, 
        file_count: fileCount, 
        is_new_access: isNewAccess,
        order_number: confirmedOrderNumber 
      } = result;

      // 并行刷新文件列表和分类列表（原来是串行的）
      if (isAuthenticated && user) {
        await Promise.all([
          fetchAccessibleFiles(),
          fetchTagsAndCategories()
        ]);
        // 触发侧边栏刷新
        setSidebarRefreshTrigger(prev => prev + 1);
      }

      // 自动导航到订单文件夹
      if (categoryId) {
        const isMobile = window.innerWidth < 768;
        const delay = isMobile ? 1200 : 500;
        
        setTimeout(() => {
          handleCategorySelect(categoryId);
          if (currentView !== 'files') {
            setCurrentView('files');
          }
        }, delay);
      }

      // 显示成功信息
      const folderMessage = categoryId ? '已为您创建专属文件夹，' : '';
      const autoNavigateMessage = categoryId ? '正在为您跳转到订单文件夹...' : '';
      const message = isNewAccess 
        ? `成功查询订单 ${confirmedOrderNumber}！${folderMessage}已为您添加 ${fileCount} 个文件的访问权限。${autoNavigateMessage}`
        : `订单 ${confirmedOrderNumber} 查询成功！您之前已获得该订单 ${fileCount} 个文件的访问权限。${autoNavigateMessage}`;
      
      toast.addToast(message, 'success', 4000);
      
    } catch (error) {
      console.error('订单查询失败:', error);
      toast.addToast('订单查询失败，请稍后重试', 'error');
    }
  };

  // ========== 以下是保留的旧代码的占位符，用于兼容性 ==========
  // 注意：以下代码块已被上面的优化版本替代，但保留注释以便理解原逻辑
  // 原来的串行查询逻辑已移至数据库存储过程 query_order_with_permissions
  const _legacyOrderQueryPlaceholder = async (_orderNumber: string) => {
    // 此函数仅作为文档保留，展示原来的逻辑流程：
    // 1. 查询订单信息 + order_files + files (嵌套查询)
    // 2. 可能调用 create_order_folder RPC
    // 3. 查询 order_user_access 检查是否首次访问
    // 4. 插入 order_user_access 记录
    // 5. 从 order_files 或 file_categories 获取文件ID
    // 6. 可能同步文件到 order_files
    // 7. 查询现有 file_permissions
    // 8. 查询 categories 获取可见性类型
    // 9. 批量插入 file_permissions
    // 10. 查询/插入 category_permissions
    // 11. 调用 fetchAccessibleFiles() (又是多次查询)
    // 12. 调用 fetchTagsAndCategories()
    // 以上所有步骤现在都在一个 RPC 调用中完成！
  };
  void _legacyOrderQueryPlaceholder; // 消除 unused 警告



  // 生成智能搜索建议（分区展示：订单 + 文件夹）
  const generateSearchSuggestions = async (query: string) => {
    if (!query.trim()) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSmartSearching(true);
    
    try {
      const searchTerm = query.trim().toLowerCase();
      const suggestions: Array<{
        type: 'file' | 'order' | 'folder';
        text: string;
        categoryId?: string;
        orderNumber?: string;
        description?: string;
      }> = [];
      
      // 1. 搜索当前租户的订单号（仅登录用户）
      if (isAuthenticated) {
        const { data: orders, error: orderError } = await supabase
          .from('orders')
          .select('id, order_number, primary_category_id, description')
          .eq('tenant', tenant)
          .or(`order_number.ilike.%${searchTerm}%`)
          .eq('is_active', true)
          .limit(3);

        if (!orderError && orders && orders.length > 0) {
          orders.forEach(order => {
            suggestions.push({
              type: 'order',
              text: order.order_number,
              description: order.description || undefined,
              categoryId: order.primary_category_id,
              orderNumber: order.order_number
            });
          });
        }
      }
      
      // 2. 搜索当前租户的文件夹/分类名称（排除受限公开文件夹）
      const { data: categories, error: categoryError } = await supabase
        .from('categories')
        .select('id, name, visibility_type')
        .eq('tenant', tenant)
        .ilike('name', `%${searchTerm}%`)
        .neq('visibility_type', 'restricted_public')
        .limit(3);

      if (!categoryError && categories && categories.length > 0) {
        // 过滤出用户有权限的文件夹
        const accessibleCategoryIds = availableCategories.map(c => c.id);
        categories
          .filter(cat => accessibleCategoryIds.includes(cat.id))
          .forEach(category => {
            suggestions.push({
              type: 'folder',
              text: category.name,
              categoryId: category.id
            });
          });
      }
      
      // 注意：不再添加"文件搜索"选项，因为按回车就是搜索文件
      
      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      
    } catch (error) {
      console.error('生成搜索建议失败:', error);
      setSearchSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSmartSearching(false);
    }
  };

  // 处理用户选择的搜索建议
  const handleSearchSuggestionSelect = (suggestion: {
    type: 'file' | 'order' | 'folder';
    text: string;
    categoryId?: string;
    orderNumber?: string;
    description?: string;
  }) => {
    setShowSuggestions(false);
    setSearchQuery(''); // 清空搜索框
    setSearchResultType(null);
    
    if (suggestion.type === 'order' && suggestion.orderNumber) {
      // 点击订单 → 执行订单查询
      handleOrderQuery(suggestion.orderNumber);
    } else if (suggestion.type === 'folder' && suggestion.categoryId) {
      // 点击文件夹 → 进入该文件夹
      handleCategorySelect(suggestion.categoryId);
    }
  };

  const filterFiles = () => {
    // 如果有搜索词且当前是文件搜索模式，则使用数据库返回的结果（已经在 files 中），不再进行本地过滤
    // 这样可以确保搜索的是全量数据库，而不仅仅是本地加载的那几个文件
    if (searchQuery.trim() && (!searchResultType || searchResultType === 'file')) {
      // 直接显示数据库返回的结果
      setFilteredFiles(files);
      return;
    }

    let filtered = files;

    if (filterType !== 'all') {
      filtered = filtered.filter(file => file.type === filterType);
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(file =>
        file.tags && file.tags.some((tag: any) => selectedTags.includes(tag.id))
      );
    }

    if (selectedCategory) {
      // 只显示当前选中文件夹中的文件，不包括子文件夹
      filtered = filtered.filter(file =>
        file.categories && file.categories.some((cat: any) => cat.id === selectedCategory)
      );
    }

    setFilteredFiles(filtered);
  };

  const getFileIcon = (type: string) => {
    const iconProps = { size: 20, className: "text-gray-500" };
    
    switch (type) {
      case 'image':
        return <Image {...iconProps} className="text-green-500" />;
      case 'video':
        return <Video {...iconProps} className="text-red-500" />;
      case 'audio':
        return <Music {...iconProps} className="text-purple-500" />;
      case 'pdf':
      case 'document':
        return <FileText {...iconProps} className="text-blue-500" />;
      default:
        return <File {...iconProps} />;
    }
  };

  const getThumbnailUrl = (thumbnailKey: string) => {
    return `https://file.unilumin-gtm.com/${thumbnailKey}`;
  };

  const getFilePreview = (file: KnowledgeFile) => {
    const getFileTypeIcon = (type: string) => {
      switch (type) {
        case 'pdf':
          return <FileText className="w-16 h-14 text-blue-500 mx-auto mb-2" />;
        case 'video':
          return <Video className="w-16 h-14 text-red-500 mx-auto mb-2" />;
        case 'audio':
          return <Music className="w-16 h-14 text-purple-500 mx-auto mb-2" />;
        case 'image':
          return <Image className="w-16 h-14 text-green-500 mx-auto mb-2" />;
        case 'document':
          return <FileText className="w-16 h-14 text-blue-500 mx-auto mb-2" />;
        default:
          return <File className="w-16 h-14 text-gray-500 mx-auto mb-2" />;
      }
    };

    // 如果有缩略图，显示缩略图
    if (file.thumbnail_key) {
      return (
        <div className="relative w-full h-48 bg-gray-100 rounded-t-xl overflow-hidden flex items-center justify-center p-2">
          <img 
            src={getThumbnailUrl(file.thumbnail_key)}
            alt={file.name}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              // 如果缩略图加载失败，显示文件类型图标
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.classList.remove('hidden');
              }
            }}
          />
          <div className="hidden w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="text-center">
              {getFileTypeIcon(file.type)}
              <p className="text-sm text-gray-500 uppercase font-medium">
                {file.type?.toUpperCase() || 'FILE'}
              </p>
            </div>
          </div>
          {/* 如果是视频文件，添加播放图标覆盖 */}
          {file.type === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
              <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                <Video className="w-8 h-8 text-red-500" />
              </div>
            </div>
          )}
        </div>
      );
    }

    // 没有缩略图，显示文件类型图标
    return (
      <div className="relative h-48 bg-gray-100 rounded-t-xl overflow-hidden flex items-center justify-center">
        <div className="text-center">
          {getFileTypeIcon(file.type)}
          <p className="text-sm text-gray-500 uppercase font-medium mt-2">
            {file.type?.toUpperCase() || 'FILE'}
          </p>
        </div>
      </div>
    );
  };

  const canPreview = (file: KnowledgeFile): boolean => {
    // 支持与管理页面相同的所有预览类型
    const previewableTypes = [
      'pdf',       // PDF文档
      'video',     // 视频 (mp4, webm, mov等)
      'image',     // 图片 (jpg, png, gif等)
      'code',      // 代码/Markdown (js, ts, py, md等)
      'document',  // Word文档 (doc, docx) - Office Online
      'spreadsheet', // Excel表格 (xls, xlsx) - Office Online
      'presentation' // PowerPoint (ppt, pptx) - Office Online
    ];
    return previewableTypes.includes(file.type);
  };

  const handlePreview = async (file: KnowledgeFile) => {
    try {
      if (user) {
        await supabase.from('public_access_logs').insert({
          content_type: 'file',
          content_id: file.id,
          user_id: user.id,
        });
      }

      if (file.storage_key) {
        const fileUrl = `https://file.unilumin-gtm.com/${file.storage_key}`;
        setPreviewFile({ ...file, url: fileUrl });
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error('预览失败:', error);
    }
  };

  const handleDownload = async (file: KnowledgeFile) => {
    if (!file.storage_key) return;
    
    try {
      if (user) {
        try {
          await supabase.from('public_access_logs').insert({
            content_type: 'file',
            content_id: file.id,
            user_id: user.id,
          });
        } catch (logError) {
          console.warn('记录访问日志失败:', logError);
        }
      }

      const { getSignedUrl } = await import('../config/storage');
      const signedUrl = await getSignedUrl(file.storage_key);
      
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Download failed:', error);
      addToast(t('common.error'), 'error');
    }
  };

  // 切换文件选择状态
  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedFiles.size === filteredFiles.length && filteredFiles.length > 0) {
      // 如果全部选中，则取消全选
      setSelectedFiles(new Set());
    } else {
      // 否则全选当前显示的所有文件
      setSelectedFiles(new Set(filteredFiles.map(f => f.id)));
    }
  };

  // 切换批量操作模式
  const toggleBatchMode = () => {
    setBatchMode(!batchMode);
    // 退出批量模式时清空选择
    if (batchMode) {
      setSelectedFiles(new Set());
    }
  };

  // 批量下载为ZIP
  const handleBatchDownload = async () => {
    if (selectedFiles.size === 0) {
      toast.addToast('请先选择要下载的文件', 'warning', 2000);
      return;
    }

    setIsDownloadingBatch(true);
    toast.addToast(`正在准备下载 ${selectedFiles.size} 个文件...`, 'info', 3000);

    try {
      const zip = new JSZip();
      const { getSignedUrl } = await import('../config/storage');
      
      // 获取选中的文件
      const filesToDownload = filteredFiles.filter(f => selectedFiles.has(f.id));
      
      // 记录访问日志
      if (user) {
        try {
          await Promise.all(
            filesToDownload.map(file =>
              supabase.from('public_access_logs').insert({
                content_type: 'file',
                content_id: file.id,
                user_id: user.id,
              })
            )
          );
        } catch (logError) {
          console.warn('记录批量访问日志失败:', logError);
        }
      }

      // 下载每个文件并添加到ZIP
      let successCount = 0;
      for (const file of filesToDownload) {
        try {
          if (!file.storage_key) continue;
          
          const signedUrl = await getSignedUrl(file.storage_key);
          const response = await fetch(signedUrl);
          
          if (response.ok) {
            const blob = await response.blob();
            zip.file(file.name, blob);
            successCount++;
            
            // 更新进度提示
            toast.addToast(`已添加 ${successCount}/${filesToDownload.length} 个文件`, 'info', 1000);
          }
        } catch (error) {
          console.error(`下载文件 ${file.name} 失败:`, error);
        }
      }

      if (successCount === 0) {
        throw new Error('没有文件成功添加到压缩包');
      }

      // 生成ZIP文件
      toast.addToast('正在生成压缩文件...', 'info', 2000);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // 下载ZIP文件
      const objectUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = objectUrl;
      
      // 使用文件夹名称作为ZIP文件名
      let zipFileName: string;
      
      if (selectedCategory && categoryPath.length > 0) {
        // 如果选择了文件夹，使用 categoryPath 获取当前文件夹名称
        const currentFolder = categoryPath[categoryPath.length - 1];
        zipFileName = `${currentFolder.name}.zip`;
      } else if (selectedCategory) {
        // 备用：从 availableCategories 查找
        const findCategoryById = (categories: PortalCategory[], id: string): PortalCategory | null => {
          for (const cat of categories) {
            if (cat.id === id) return cat;
            if (cat.children) {
              const found = findCategoryById(cat.children, id);
              if (found) return found;
            }
          }
          return null;
        };
        const folder = findCategoryById(availableCategories, selectedCategory);
        zipFileName = folder ? `${folder.name}.zip` : '文件.zip';
      } else {
        // 如果没有选择文件夹（批量选择多个文件），使用日期格式
        const now = new Date();
        const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
        zipFileName = `批量文件_${dateStr}.zip`;
      }
      
      link.download = zipFileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      toast.addToast(`成功下载 ${successCount} 个文件！`, 'success', 3000);
      
      // 清空选择
      setSelectedFiles(new Set());
    } catch (error) {
      console.error('批量下载失败:', error);
      toast.addToast(`批量下载失败: ${error instanceof Error ? error.message : '未知错误'}`, 'error', 5000);
    } finally {
      setIsDownloadingBatch(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // 个人中心相关函数
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    try {
      setIsUploadingAvatar(true);
      // 提取文件扩展名，使用纯 ASCII 文件名避免 Supabase Storage 错误
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const storageKey = `${user.id}/${Date.now()}.${fileExt}`;
      
      // 显式根据后缀名判断 MIME 类型，作为兜底
      let mimeType = file.type;
      if (!mimeType) {
        if (fileExt === 'png') mimeType = 'image/png';
        else if (fileExt === 'gif') mimeType = 'image/gif';
        else if (fileExt === 'webp') mimeType = 'image/webp';
        else mimeType = 'image/jpeg';
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(storageKey, file, {
          contentType: mimeType, 
          cacheControl: '3600',
          upsert: true
        });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(storageKey);

      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: publicUrl, avatar_storage_path: storageKey })
        .eq('id', user.id);
      if (updateError) throw updateError;

      setUser({ ...user, avatar_url: publicUrl });
      // 触发事件通知其他组件（如 PublicHeader）刷新头像
      window.dispatchEvent(new CustomEvent('avatar-updated'));
      addToast(t('profilePage.avatarUpdated') || 'Avatar updated successfully', 'success');
    } catch (error) {
      console.error('Avatar update failed:', error);
      addToast(t('profilePage.avatarUpdateFailed'), 'error');
    } finally {
      setIsUploadingAvatar(false);
      // 清空文件输入，避免选择相同文件无法触发change
      (e.target as HTMLInputElement).value = '';
    }
  };

  const handleUpdateProfile = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('users')
        .update({
          name: profileForm.name
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      // 更新本地用户状态
      setUser({ ...user, name: profileForm.name });
      setIsEditingProfile(false);
      addToast(t('profilePage.profileUpdated'), 'success');
    } catch (error) {
      console.error('Profile update failed:', error);
      addToast(t('profilePage.profileUpdateFailed'), 'error');
    }
  };

  const handleUpdatePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      addToast(t('profilePage.passwordMismatch'), 'error');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      addToast(t('profilePage.passwordTooShort'), 'error');
      return;
    }
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      });
      
      if (error) throw error;
      
      setPasswordForm({
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
      addToast(t('profilePage.passwordUpdated'), 'success');
    } catch (error) {
      console.error('Password update failed:', error);
      addToast(t('profilePage.passwordUpdateFailed'), 'error');
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setIsAuthenticated(false);
      setCurrentView('files');
    } catch (error) {
      console.error('Sign out failed:', error);
      addToast(t('profilePage.signOutFailed'), 'error');
    }
  };

  // 个人中心页面内容
  const renderProfileContent = () => {
    if (!isAuthenticated || !user) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400 mb-4">{t('profilePage.loginRequired')}</div>
        </div>
      );
    }

    return (
      <div className="px-4 py-4 max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('profilePage.personalInfo')}</h1>
          <button
            onClick={() => setCurrentView('files')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-xl transition-all"
          >
            <X className="w-4 h-4" />
            <span>{t('profilePage.backToFiles')}</span>
          </button>
        </div>
        
        {/* 用户基本信息 */}
        <div className="bg-white dark:bg-white/[0.05] rounded-2xl shadow-sm dark:shadow-none border border-gray-200 dark:border-white/10 p-5 mb-4 backdrop-blur-sm">
          <div className="flex items-center mb-3">
            <label className="relative w-16 h-16 flex-shrink-0 cursor-pointer group">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-teal-100 dark:bg-teal-500/20">
                {user.avatar_url ? (
                  <img 
                    src={user.avatar_url} 
                    alt={user.name} 
                    className="w-full h-full object-cover aspect-square" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-2xl font-semibold text-teal-600 dark:text-teal-400">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
              {isUploadingAvatar && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center text-white text-xs">
                  {t('profilePage.uploading')}
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-full" />
            </label>
            <div className="ml-3 min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {user.name || t('profilePage.nameNotSet')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{user.email}</p>
              <span className={cn(
                "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1.5",
                PERMISSION_COLORS[user.role]
              )}>
                {PERMISSION_LABELS[user.role]}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-500">{t('profilePage.clickToChangeAvatar')}</p>
        </div>

        {/* 个人资料编辑 */}
        <div className="bg-white dark:bg-white/[0.05] rounded-2xl shadow-sm dark:shadow-none border border-gray-200 dark:border-white/10 p-5 mb-4 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('profilePage.personalInfo')}</h3>
            {!isEditingProfile && (
              <button
                onClick={() => setIsEditingProfile(true)}
                className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium transition-colors"
              >
                {t('profilePage.edit')}
              </button>
            )}
          </div>

          {isEditingProfile ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('profilePage.name')}</label>
                <input
                  type="text"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-white/10 border border-gray-300 dark:border-white/20 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleUpdateProfile}
                  className="bg-teal-600 dark:bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors font-medium"
                >
                  {t('profilePage.save')}
                </button>
                <button
                  onClick={() => {
                    setIsEditingProfile(false);
                    setProfileForm({
                      name: user.name || ''
                    });
                  }}
                  className="bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-white/20 transition-colors font-medium"
                >
                  {t('profilePage.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-16">{t('profilePage.name')}</span>
                <span className="text-gray-900 dark:text-white">{user.name || t('notSet')}</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-16">{t('profilePage.email')}</span>
                <span className="text-gray-900 dark:text-white">{user.email}</span>
              </div>
            </div>
          )}
        </div>

        {/* 密码修改 */}
        <div className="bg-white dark:bg-white/[0.05] rounded-2xl shadow-sm dark:shadow-none border border-gray-200 dark:border-white/10 p-5 mb-4 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('profilePage.passwordSettings')}</h3>
            {!showPasswordForm && (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium transition-colors"
              >
                {t('profilePage.changePasswordBtn')}
              </button>
            )}
          </div>

          {showPasswordForm ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('profilePage.newPassword')}</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-white/10 border border-gray-300 dark:border-white/20 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                  placeholder={t('profilePage.newPasswordPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('profilePage.confirmNewPassword')}</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-white/10 border border-gray-300 dark:border-white/20 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                  placeholder={t('profilePage.confirmPasswordPlaceholder')}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleUpdatePassword}
                  className="bg-teal-600 dark:bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors font-medium"
                >
                  {t('profilePage.updatePassword')}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordForm({
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-white/20 transition-colors font-medium"
                >
                  {t('profilePage.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400 text-sm">{t('profilePage.passwordHint')}</p>
          )}
        </div>

        {/* 退出登录 */}
        <div className="bg-white dark:bg-white/[0.05] rounded-2xl shadow-sm dark:shadow-none border border-gray-200 dark:border-white/10 p-5 backdrop-blur-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('profilePage.accountActions')}</h3>
          <button
            onClick={handleSignOut}
            className="bg-red-600 dark:bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors font-medium"
          >
            {t('profilePage.signOut')}
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    // 使用 500ms 延迟加载原则：快速加载时不显示任何加载状态
    // 只有超过 500ms 才显示骨架屏，避免闪烁
    if (!showLoading) {
      return (
        <PublicLayout
          selectedTags={selectedTags}
          selectedCategory={selectedCategory}
          onTagToggle={handleTagToggle}
          onCategorySelect={handleCategorySelect}
          onResetFilters={resetAllFilters}
          onOrderQuery={handleOrderQuery}
          sidebarRefreshTrigger={sidebarRefreshTrigger}
          user={user}
          isAuthenticated={isAuthenticated}
          currentView={currentView}
          onViewChange={setCurrentView}
        >
          {/* 快速加载时显示空白，避免闪烁 */}
          <div className="h-64" />
        </PublicLayout>
      );
    }
    
    return (
      <PublicLayout
        selectedTags={selectedTags}
        selectedCategory={selectedCategory}
        onTagToggle={handleTagToggle}
        onCategorySelect={handleCategorySelect}
        onResetFilters={resetAllFilters}
        onOrderQuery={handleOrderQuery}
        sidebarRefreshTrigger={sidebarRefreshTrigger}
        user={user}
        isAuthenticated={isAuthenticated}
        currentView={currentView}
        onViewChange={setCurrentView}
      >
        {/* 超过 500ms 显示骨架屏 */}
        <div className="space-y-6 p-4">
          {/* 标题骨架 */}
          <div className="flex items-center justify-between">
            <div 
              className="h-6 rounded skeleton-shimmer" 
              style={{ width: '200px', backgroundColor: 'var(--color-background-tertiary)' }} 
            />
            <div className="flex gap-2">
              <div 
                className="h-9 w-9 rounded-lg skeleton-shimmer" 
                style={{ backgroundColor: 'var(--color-background-tertiary)' }} 
              />
              <div 
                className="h-9 w-9 rounded-lg skeleton-shimmer" 
                style={{ backgroundColor: 'var(--color-background-tertiary)' }} 
              />
            </div>
          </div>
          
          {/* 文件网格骨架 */}
          {viewMode === 'grid' ? (
            <SkeletonGrid count={8} columns={4} />
          ) : (
            <SkeletonList count={6} />
          )}
        </div>
      </PublicLayout>
    );
  }


  return (
    <PublicLayout
      selectedTags={selectedTags}
      selectedCategory={selectedCategory}
      onTagToggle={handleTagToggle}
      onCategorySelect={handleCategorySelect}
        onResetFilters={resetAllFilters}
        onOrderQuery={handleOrderQuery}
        sidebarRefreshTrigger={sidebarRefreshTrigger}
        user={user}
        isAuthenticated={isAuthenticated}
        currentView={currentView}
        onViewChange={setCurrentView}
    >
      <div className="h-full flex flex-col">
        {/* 根据当前视图显示不同内容 */}
        {currentView === 'profile' ? (
          <div className="flex-1 overflow-y-auto">
            {renderProfileContent()}
          </div>
        ) : currentView === 'order' ? (
          <div className="flex-1 overflow-y-auto">
            <MobileOrderQuery 
              user={user}
              isAuthenticated={isAuthenticated}
              onOrderQuery={handleOrderQuery}
            />
          </div>
        ) : (
          <>
        {/* 顶部固定控件区域 */}
        <div className="flex-shrink-0 space-y-4 mb-6">
          {/* 状态提示 */}
          {!isAuthenticated && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center">
                <div>
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    {t('portal.guestBannerTitle', { count: filteredFiles.length })}
                  </h3>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    {t('portal.guestBannerDescription')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 面包屑导航 - 支持横向滚动 */}
          {categoryPath.length > 0 && (
            <div className="flex items-center space-x-2 text-sm overflow-x-auto pb-2 scrollbar-hide no-scrollbar portal-breadcrumb-mobile">
              <button
                onClick={() => handleCategorySelect(null)}
                className="flex items-center text-teal-600 hover:text-teal-700 whitespace-nowrap"
              >
                <Home className="w-4 h-4 mr-1" />
                {t('portal.allFilesHome')}
              </button>
              {categoryPath.map((category, index) => (
                <div key={category.id} className="flex items-center">
                  <ChevronRight className="w-4 h-4 text-gray-400 mx-1 flex-shrink-0" />
                  <button
                    onClick={() => handleCategorySelect(category.id)}
                    className={cn(
                      "text-teal-600 hover:text-teal-700 whitespace-nowrap",
                      index === categoryPath.length - 1 && "font-medium text-gray-900 dark:text-gray-100"
                    )}
                  >
                    {category.name}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 文件夹统计信息 */}
          {selectedCategory && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4 px-1">
              <span className="bg-gray-100 dark:bg-white/10 px-2 py-0.5 rounded-md font-medium text-gray-700 dark:text-gray-300">
                {totalFilesCount} {t('filesLabel', 'files')}
              </span>
              <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600"></span>
              <span className="text-teal-600 dark:text-teal-400 font-medium font-mono">
                {formatFileSize(totalFilesSize)}
              </span>
              {isLoadingMore && (
                <>
                  <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600"></span>
                  <span className="text-gray-400 animate-pulse">Loading...</span>
                </>
              )}
            </div>
          )}

          {/* 搜索和控件栏 - 始终显示，适配深色模式 */}
          <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className={cn(
                "absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors",
                isSmartSearching ? "text-teal-500 animate-pulse" : "text-gray-400 dark:text-gray-500"
              )} />
              <input
                type="text"
                placeholder={t('portal.searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => {
                  if (searchQuery && searchSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => {
                  // 延迟隐藏建议，允许用户点击建议
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                className="w-full pl-10 pr-20 md:pr-24 py-2.5 bg-white dark:bg-white/10 border border-gray-300 dark:border-white/20 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all outline-none md:h-12 portal-inputs-mobile"
              />
              
              {/* 搜索按钮 */}
              <button
                onClick={handleSearchSubmit}
                disabled={!searchQuery.trim() || isSearching}
                className={cn(
                  "absolute right-1.5 top-1/2 transform -translate-y-1/2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                  searchQuery.trim() && !isSearching
                    ? "bg-teal-500 hover:bg-teal-600 text-white shadow-sm"
                    : "bg-gray-100 dark:bg-white/10 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                )}
              >
                {isSearching ? (
                  <span className="flex items-center">
                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  </span>
                ) : (
                  t('common.search')
                )}
              </button>
              
              {/* 🆕 Apple 风格搜索建议下拉框 - 分区展示 */}
              {showSuggestions && searchQuery.trim() && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl dark:shadow-black/40 z-50 overflow-hidden">
                  {/* 订单区域 */}
                  {searchSuggestions.filter(s => s.type === 'order').length > 0 && (
                    <div className="border-b border-gray-100 dark:border-white/5">
                      <div className="px-4 py-2 bg-gray-50/80 dark:bg-white/5">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          📋 {t('portal.order')}
                        </span>
                      </div>
                      {searchSuggestions.filter(s => s.type === 'order').map((suggestion, index) => (
                        <button
                          key={`order-${index}`}
                          onClick={() => handleSearchSuggestionSelect(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {suggestion.text}
                              </div>
                              {suggestion.description && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {suggestion.description}
                                </div>
                              )}
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors">
                              {t('portal.searchNow')} →
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* 文件夹区域 */}
                  {searchSuggestions.filter(s => s.type === 'folder').length > 0 && (
                    <div className="border-b border-gray-100 dark:border-white/5">
                      <div className="px-4 py-2 bg-gray-50/80 dark:bg-white/5">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          📁 {t('portal.folder')}
                        </span>
                      </div>
                      {searchSuggestions.filter(s => s.type === 'folder').map((suggestion, index) => (
                        <button
                          key={`folder-${index}`}
                          onClick={() => handleSearchSuggestionSelect(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {suggestion.text}
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors">
                              {t('common.open')} →
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* 底部提示：按回车搜索文件 */}
                  <div className="px-4 py-3 bg-gray-50/50 dark:bg-white/[0.02]">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-2">
                        <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px] font-mono">
                          ↵ Enter
                        </kbd>
                        <span>{t('portal.pressEnterToSearch')}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px] font-mono">
                          Esc
                        </kbd>
                        <span>{t('common.close')}</span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* 类型筛选 - 移动端自适应宽度 */}
            <div className="relative flex-1 sm:flex-none sm:w-48 min-w-0">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="w-full pl-3 pr-8 py-2.5 bg-white dark:bg-white/10 border border-gray-300 dark:border-white/20 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 appearance-none cursor-pointer h-11 sm:h-12 text-sm sm:text-base font-medium truncate"
              >
                <option value="all">{t('portal.allTypes')}</option>
                <option value="image">{t('fileType.image')}</option>
                <option value="document">{t('fileType.document')}</option>
                <option value="video">{t('fileType.video')}</option>
                <option value="audio">{t('fileType.audio')}</option>
              </select>
              <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <ChevronDown className="w-4 h-4" />
              </div>
            </div>

            {/* 功能按钮组 - 移动端紧凑布局 */}
            <div className="flex items-center gap-1 shrink-0">
              {/* 视图切换 */}
              <div className="flex bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-0.5 sm:p-1 h-11 sm:h-12">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    "px-2.5 sm:px-3 rounded-lg transition-all flex items-center justify-center",
                    viewMode === 'grid' 
                      ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-blue-400 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  )}
                >
                  <Grid className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    "px-2.5 sm:px-3 rounded-lg transition-all flex items-center justify-center",
                    viewMode === 'list' 
                      ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-blue-400 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  )}
                >
                  <List className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* 批量多选按钮 */}
              {filteredFiles.length > 0 && (
                <button
                  onClick={toggleBatchMode}
                  className={cn(
                    "w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl transition-all duration-300 border",
                    batchMode
                      ? "bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/25"
                      : "bg-white dark:bg-white/10 border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-50"
                  )}
                  title={batchMode ? t('portal.exitBatchMode') : t('portal.batchActions')}
                >
                  <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

          {/* 批量操作工具栏 - 只在批量模式下显示 */}
          {batchMode && filteredFiles.length > 0 && (
            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200/50 dark:border-blue-500/20 animate-fade-in">
              {/* 全选按钮 */}
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-white/10 border border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/15 transition-colors"
                title={selectedFiles.size === filteredFiles.length ? t('portal.deselectAll') : t('portal.selectAll')}
              >
                {selectedFiles.size === filteredFiles.length && filteredFiles.length > 0 ? (
                  <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                ) : (
                  <Square className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-700 dark:text-white">
                  {selectedFiles.size === filteredFiles.length && filteredFiles.length > 0 ? t('portal.deselectAll') : t('portal.selectAll')}
                </span>
              </button>

              {/* 选中数量 */}
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span>{t('portal.selectedItems', { count: selectedFiles.size })}</span>
                <span>/ {filteredFiles.length} {t('filesTitle')}</span>
              </div>

              {/* 批量下载按钮 */}
              {selectedFiles.size > 0 && (
                <button
                  onClick={handleBatchDownload}
                  disabled={isDownloadingBatch}
                  className={cn(
                    "ml-auto flex items-center gap-2 px-5 py-2 rounded-lg font-semibold transition-all duration-200",
                    isDownloadingBatch
                      ? "bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl"
                  )}
                >
                  {isDownloadingBatch ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>{t('common.downloading')}</span>
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4" />
                      <span>{t('portal.downloadSelected')} ({selectedFiles.size})</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* 当前筛选状态 */}
          {(selectedTags.length > 0 || selectedCategory || searchQuery || filterType !== 'all') && (
            <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm text-gray-600">{t('portal.currentFilter')}:</span>
            
            {searchQuery && (
              <span className={cn(
                "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                searchResultType === 'order' 
                  ? "bg-orange-100 text-orange-800" 
                  : searchResultType === 'folder'
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800"
              )}>
                {searchResultType === 'order' && `${t('portal.orderSearch')}: `}
                {searchResultType === 'folder' && `${t('portal.folderSearch')}: `}
                {(!searchResultType || searchResultType === 'file') && `${t('portal.fileSearch')}: `}
                {searchQuery}
              </span>
            )}
            
            {filterType !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                {t('portal.typeLabel')}: {filterType}
              </span>
            )}
            
            {selectedCategory && categoryPath.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-teal-100 text-teal-800">
                {t('portal.folderLabel')}: {categoryPath[categoryPath.length - 1].name}
              </span>
            )}
            
            {selectedTags.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                {t('portal.tagsLabel')}: {t('portal.tagsCount', { count: selectedTags.length })}
              </span>
            )}
            
            <button
              onClick={resetAllFilters}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800 hover:bg-red-200"
            >
              <X className="w-3 h-3 mr-1" />
              {t('portal.clearAll')}
            </button>
          </div>
        )}
        </div>

        {/* 可滚动的内容区域 */}
        <div className="flex-1 overflow-y-auto">
          {/* 显示子文件夹 */}
          {selectedCategory && (() => {
            const findCategoryById = (categories: PortalCategory[], id: string): PortalCategory | null => {
              for (const cat of categories) {
                if (cat.id === id) return cat;
                if (cat.children) {
                  const found = findCategoryById(cat.children, id);
                  if (found) return found;
                }
              }
              return null;
            };
            const currentCategory = findCategoryById(availableCategories, selectedCategory);
            
            // 递归检查文件夹及其子文件夹是否包含文件
            const hasFilesInBranch = (cat: PortalCategory): boolean => {
              if ((cat.file_count || 0) > 0) return true;
              if (cat.children && cat.children.length > 0) {
                return cat.children.some(child => hasFilesInBranch(child));
              }
              return false;
            };

            // 过滤子文件夹：只显示有内容的文件夹分支
            const subFolders = (currentCategory?.children || []).filter(cat => hasFilesInBranch(cat));
            
            if (subFolders.length > 0) {
              return (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-gray-400 dark:text-gray-500 mb-4 px-1 flex items-center uppercase tracking-widest">
                    <Folder className="w-3.5 h-3.5 mr-2 opacity-60" />
                    {t('portal.subfoldersCount', { count: subFolders.length })}
                  </h3>
                  <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                    {subFolders.map((folder) => {
                      // 使用预获取的 file_count 而不是 local files 过滤，避免层级加载问题
                      const folderFileCount = folder.file_count || 0;
                      
                      return (
                        <button
                          key={folder.id}
                          onClick={() => handleCategorySelect(folder.id)}
                          className="flex items-center p-3 md:p-4 bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 hover:border-blue-300 dark:hover:border-blue-500/30 hover:bg-blue-50/30 transition-all duration-200 group portal-subfolder-button-mobile"
                        >
                          <div className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-500/20 dark:to-indigo-500/20 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                            <Folder className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="ml-3 flex-1 min-w-0 text-left">
                            <p className="font-bold text-gray-900 dark:text-white truncate text-[14px] md:text-sm">{folder.name}</p>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                              {folderFileCount > 0 ? `${folderFileCount} ${t('filesTitle')}` : t('portal.emptyFolder')}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-blue-500 transition-colors flex-shrink-0 ml-2" />
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-6 mb-4 border-t border-gray-200"></div>
                </div>
              );
            }
            return null;
          })()}
          
          {filteredFiles.length === 0 && !selectedCategory && !searchQuery && filterType === 'all' && selectedTags.length === 0 ? (() => {
            // 递归检查文件夹及其子文件夹是否包含文件
            const checkHasFiles = (c: PortalCategory): boolean => {
              if ((c.file_count || 0) > 0) return true;
              if (c.children && c.children.length > 0) {
                return c.children.some(child => checkHasFiles(child));
              }
              return false;
            };

            const visibleCategories = availableCategories.filter(cat => checkHasFiles(cat));

            return (
              <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 animate-fade-in">
                {visibleCategories.map((category) => {
                  const visibleChildrenCount = category.children?.filter(c => checkHasFiles(c)).length || 0;
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category.id)}
                      className="bg-white dark:bg-white/[0.1] p-6 md:p-8 rounded-[32px] border border-gray-100/50 dark:border-white/10 hover:border-teal-300 dark:hover:border-teal-500/40 hover:shadow-2xl hover:shadow-teal-500/10 dark:hover:shadow-teal-900/20 hover:-translate-y-1.5 transition-all duration-500 group text-left relative overflow-hidden portal-folder-card-premium"
                    >
                      {/* 背景装饰图案 */}
                      <div className="absolute -top-10 -right-10 opacity-5 group-hover:opacity-10 transition-all duration-700 group-hover:scale-110">
                         <Folder className="w-40 h-40 text-teal-600 dark:text-teal-400 rotate-12" />
                      </div>
                      
                      <div className="relative z-10 w-full flex flex-col items-center">
                        <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-500/30 dark:to-emerald-500/20 rounded-[28px] flex items-center justify-center mb-6 group-hover:rotate-3 transition-all duration-500 shadow-sm border border-white/50">
                          <Folder className="w-10 h-10 md:w-12 md:h-12 text-teal-600 dark:text-teal-400 drop-shadow-sm" />
                        </div>
                        
                        <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white mb-4 line-clamp-1 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors text-center">
                          {category.name}
                        </h3>
                        
                        {/* 移动端优化统计栏 */}
                        <div className="portal-folder-stats-row border-t border-gray-100/50 dark:border-white/5">
                          <div className="portal-folder-stat-item">
                            <span className="portal-folder-stat-value">{category.file_count || 0}</span>
                            <span className="portal-folder-stat-label">{t('filesTitle')}</span>
                          </div>
                          
                          {visibleChildrenCount > 0 && (
                            <>
                              <div className="w-px h-6 bg-gray-100 dark:bg-white/10 mx-2" />
                              <div className="portal-folder-stat-item">
                                <span className="portal-folder-stat-value">{visibleChildrenCount}</span>
                                <span className="portal-folder-stat-label text-teal-600/70">{t('portal.folders')}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                
                {visibleCategories.length === 0 && (
                <div className="col-span-full py-16 text-center">
                  <div className="w-20 h-20 bg-gray-50 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Folder className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                    {t('portal.noPublicFiles')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    {t('portal.guestBannerDescription')}
                  </p>
                </div>
              )}
            </div>
          );
        })() : (
             filteredFiles.length === 0 && (() => {
               // Empty state for when a category IS selected or search IS active
               
               // 🆕 如果正在加载且选中了文件夹，显示加载动画而不是空状态
               if (isLoadingMore && selectedCategory) {
                 return (
                   <div className="flex flex-col items-center justify-center py-12">
                     <Loader />
                     <p className="text-sm text-gray-500 mt-4">{t('common.loading')}</p>
                   </div>
                 );
               }
               
               // 检查是否有子文件夹
               if (selectedCategory) {
                 const findCategoryById = (categories: PortalCategory[], id: string): PortalCategory | null => {
                  for (const cat of categories) {
                    if (cat.id === id) return cat;
                    if (cat.children) {
                      const found = findCategoryById(cat.children, id);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                const currentCategory = findCategoryById(availableCategories, selectedCategory);
                const hasSubFolders = currentCategory?.children && currentCategory.children.length > 0;
                if (hasSubFolders) return null;
               }

               return (
                <div className="text-center py-12">
                  <File className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchQuery || filterType !== 'all' ? t('portal.noMatchingFiles') : t('portal.noPublicFiles')}
                  </h3>
                  <p className="text-gray-500">
                    {!isAuthenticated ? t('portal.loginToSeeMore') : t('common.retry')}
                  </p>
                </div>
              );
            })()
          )}
          
          <div className="relative">
            {/* 搜索时的局部加载状态 */}
            {isSearching && (
              <div className="absolute inset-0 z-20 bg-white/40 dark:bg-black/20 backdrop-blur-[2px] rounded-2xl flex items-center justify-center animate-fade-in py-20">
                <div className="flex flex-col items-center gap-3 p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-white/10">
                  <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">正在搜索...</span>
                </div>
              </div>
            )}
          
          {filteredFiles.length > 0 && (
            <>
              {/* 🆕 搜索结果标题 or 文件夹内文件标题 */}
              {(selectedCategory || searchResultType === 'file') && (
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 px-1 flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
                  {searchResultType === 'file'
                    ? `${t('portal.searchResults')} (${filteredFiles.length})`
                    : `${t('filesTitle')} (${filteredFiles.length})`
                  }
                </h3>
              )}
              {viewMode === 'list' ? (
          /* 列表模式 - Apple 风格重新设计，移除斑马纹 */
          <div className="portal-list-container bg-white/90 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200/60 dark:border-white/[0.08] overflow-hidden shadow-lg dark:shadow-2xl dark:shadow-black/20">
            {/* 桌面端表头 - 简化为 flexbox 布局 */}
            <div className="hidden md:flex items-center gap-4 bg-gray-50/90 dark:bg-white/[0.04] px-6 py-4 border-b border-gray-200/60 dark:border-white/[0.08]">
              {/* 选择列 */}
              {batchMode && (
                <div className="w-10 flex-shrink-0 text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wider">
                  {t('portal.headerSelect')}
                </div>
              )}
              {/* 名称列 - flex-grow 占据剩余空间 */}
              <div className="flex-1 min-w-0 text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wider">
                {t('portal.headerName')}
              </div>
              {/* 类型列 - 固定宽度 */}
              <div className="w-28 flex-shrink-0 text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wider">
                {t('portal.headerType')}
              </div>
              {/* 大小列 - 固定宽度 */}
              <div className="w-24 flex-shrink-0 text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wider">
                {t('portal.headerSize')}
              </div>
              {/* 操作列 - 固定宽度 */}
              <div className="w-20 flex-shrink-0 text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wider text-right">
                {t('portal.headerActions')}
              </div>
            </div>
            
            <div className="divide-y divide-gray-100/80 dark:divide-white/[0.06]">
              {filteredFiles.map((file) => (
                <div
                  key={file.id}
                  className={cn(
                    "group transition-all duration-200",
                    /* 移除斑马纹，使用统一背景 */
                    "bg-transparent",
                    /* 选中状态 */
                    selectedFiles.has(file.id) 
                      ? 'bg-blue-50/80 dark:bg-blue-500/15' 
                      : 'hover:bg-gray-50/80 dark:hover:bg-white/[0.04]'
                  )}
                >
                  {/* 桌面端布局 - 使用 flexbox 与表头对齐 */}
                  <div className="hidden md:flex items-center gap-4 px-6 py-4">
                    {/* 复选框 - 只在批量模式下显示 */}
                    {batchMode && (
                      <div className="w-10 flex-shrink-0 flex items-center justify-center">
                        <button
                          onClick={() => toggleFileSelection(file.id)}
                          className="p-1.5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {selectedFiles.has(file.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400 dark:text-white/40 group-hover:text-gray-500 dark:group-hover:text-white/50" />
                          )}
                        </button>
                      </div>
                    )}
                    
                    {/* 名称列 - flex-grow 占据剩余空间 */}
                    <div className="flex-1 min-w-0 flex items-center">
                      <div className="flex-shrink-0 mr-3">
                        {getFileIcon(file.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900 dark:text-white truncate text-sm" title={file.name}>
                            {file.name}
                          </h3>
                          {/* 🆕 搜索结果中显示来源文件夹 */}
                          {searchResultType === 'file' && (file as any)._searchCategoryId && (() => {
                            const findCategoryById = (categories: PortalCategory[], id: string): PortalCategory | null => {
                              for (const cat of categories) {
                                if (cat.id === id) return cat;
                                if (cat.children) {
                                  const found = findCategoryById(cat.children, id);
                                  if (found) return found;
                                }
                              }
                              return null;
                            };
                            const sourceCat = findCategoryById(availableCategories, (file as any)._searchCategoryId);
                            if (sourceCat) {
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCategorySelect(sourceCat.id);
                                    setSearchQuery('');
                                    setSearchResultType(null);
                                  }}
                                  className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-teal-100 dark:hover:bg-teal-500/20 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                                >
                                  <Folder className="w-3 h-3" />
                                  {sourceCat.name}
                                </button>
                              );
                            }
                            return null;
                          })()}
                          <span className={cn(
                            "flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border",
                            file.is_public 
                              ? "bg-green-50 dark:bg-green-500/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-500/30"
                              : "bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/30"
                          )}>
                            {file.is_public ? t('permissions.public') : t(`permissions.${file.min_permission_level || 'basic'}`)}
                          </span>
                          {/* 标签内联显示 */}
                          {file.tags && file.tags.length > 0 && (
                            <div className="hidden lg:flex flex-shrink-0 items-center gap-1">
                              {file.tags.slice(0, 2).map((tag: any) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                                  style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                              {file.tags.length > 2 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white/50">
                                  +{file.tags.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* 类型列 - 固定宽度 */}
                    <div className="w-28 flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100/80 dark:bg-white/[0.08] text-gray-600 dark:text-white/70 uppercase">
                        {file.type}
                      </span>
                    </div>
                    
                    {/* 大小列 - 固定宽度 */}
                    <div className="w-24 flex-shrink-0">
                      <span className="text-sm text-gray-500 dark:text-white/50">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    
                    {/* 操作列 - 固定宽度 */}
                    <div className="w-20 flex-shrink-0 flex justify-end gap-1">
                      <button
                        onClick={() => handlePreview(file)}
                        disabled={!canPreview(file)}
                        className={cn(
                          "p-2 rounded-lg transition-all",
                          canPreview(file)
                            ? "text-gray-400 dark:text-white/40 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/20"
                            : "text-gray-300 dark:text-white/20 cursor-not-allowed"
                        )}
                        title="预览"
                      >
                        {canPreview(file) ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDownload(file)}
                        className="p-2 text-gray-400 dark:text-white/40 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/20 rounded-lg transition-all"
                        title="下载"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* 移动端布局 - 更紧凑的设计 */}
                  <div className="md:hidden px-4 py-3">
                    {/* 第一行：复选框（批量模式）+ 文件图标 + 标题 + 权限标识 */}
                    <div className="flex items-center mb-2">
                      {/* 复选框 - 只在批量模式下显示 */}
                      {batchMode && (
                        <button
                          onClick={() => toggleFileSelection(file.id)}
                          className="p-1.5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg transition-colors mr-2"
                        >
                          {selectedFiles.has(file.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400 dark:text-white/40" />
                          )}
                        </button>
                      )}
                      <div className="flex-shrink-0 mr-3">
                        {getFileIcon(file.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate" title={file.name}>
                          {file.name}
                        </h3>
                      </div>
                      <span className={cn(
                        "inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold ml-2 flex-shrink-0 border",
                        file.is_public 
                          ? "bg-green-50 dark:bg-green-500/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-500/30"
                          : "bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/30"
                      )}>
                        {file.is_public ? t('permissions.public') : t(`permissions.${file.min_permission_level || 'basic'}`)}
                      </span>
                    </div>
                    
                    {/* 第二行：标签 + 描述 + 文件大小 + 操作按钮 */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex items-center flex-wrap gap-1 text-xs">
                          {/* 标签 */}
                          {file.tags && file.tags.length > 0 && (
                            <>
                              {file.tags.slice(0, 2).map((tag: any) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                                  style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                              {file.tags.length > 2 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white/50 text-[10px]">
                                  +{file.tags.length - 2}
                                </span>
                              )}
                            </>
                          )}
                          
                          {/* 文件大小 */}
                          <span className="text-gray-400 dark:text-white/40 ml-1">
                            {formatFileSize(file.size)}
                          </span>
                        </div>
                        
                        {/* 描述信息 */}
                        {file.description && (
                          <p className="text-xs text-gray-400 dark:text-white/40 truncate mt-1">
                            {file.description}
                          </p>
                        )}
                      </div>
                      
                      {/* 操作按钮 */}
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => handlePreview(file)}
                          disabled={!canPreview(file)}
                          className={cn(
                            "p-2 rounded-lg transition-all",
                            canPreview(file)
                              ? "text-gray-500 dark:text-white/60 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/20 active:bg-blue-100"
                              : "text-gray-300 dark:text-white/20 cursor-not-allowed"
                          )}
                        >
                          {canPreview(file) ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDownload(file)}
                          className="p-2 text-gray-500 dark:text-white/60 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/20 active:bg-green-100 rounded-lg transition-all"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* 网格模式 - Apple 风格大卡片布局 */
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 md:gap-5">
            {filteredFiles.map((file) => {
              // 🆕 获取搜索结果的来源文件夹信息
              const searchCategoryId = (file as any)._searchCategoryId;
              let sourceCategory: PortalCategory | null = null;
              
              // 🔧 修复：只要是搜索模式且有来源文件夹ID，就显示来源文件夹
              // 无论是全局搜索还是在某个文件夹内搜索
              if (searchResultType === 'file' && searchCategoryId) {
                const findCategoryById = (categories: PortalCategory[], id: string): PortalCategory | null => {
                  for (const cat of categories) {
                    if (cat.id === id) return cat;
                    if (cat.children) {
                      const found = findCategoryById(cat.children, id);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                sourceCategory = findCategoryById(availableCategories, searchCategoryId);
              }
              
              return (
                <PortalFileCard
                  key={file.id}
                  file={file}
                  thumbnailUrl={file.thumbnail_key ? getThumbnailUrl(file.thumbnail_key) : undefined}
                  onPreview={handlePreview}
                  onDownload={handleDownload}
                  canPreview={canPreview(file)}
                  selectable={batchMode}
                  selected={selectedFiles.has(file.id)}
                  onToggleSelect={toggleFileSelection}
                  sourceFolder={sourceCategory ? { id: sourceCategory.id, name: sourceCategory.name } : undefined}
                  onSourceFolderClick={(folderId) => {
                    handleCategorySelect(folderId);
                    setSearchQuery('');
                    setSearchResultType(null);
                  }}
                />
              );
            })}
          </div>
        )}
      </>
    )}

    {/* 分页控制按钮 */}
    {hasMoreFiles && filteredFiles.length > 0 && selectedCategory && !searchQuery && (
      <div className="flex flex-col items-center justify-center gap-4 py-8 mt-4 animate-fade-in">
        <div className="flex items-center gap-4">
          <button
            onClick={() => fetchCategoryFilesForPortal(selectedCategory, false, LOAD_MORE_SIZE)}
            disabled={isLoadingMore}
            className="group flex items-center gap-2 px-6 py-2.5 rounded-full bg-white dark:bg-white/10 border border-gray-200 dark:border-white/20 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/15 hover:border-gray-300 dark:hover:border-white/30 transition-all shadow-sm hover:shadow-md active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoadingMore ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                <span>{t('common.loading', '加载中...')}</span>
              </>
            ) : (
              <>
                <ChevronRight className="w-4 h-4 rotate-90 transition-transform group-hover:translate-y-0.5" />
                <span>{t('portal.loadMore', '加载更多文件')}</span>
                <span className="text-xs text-gray-400 font-normal ml-1">
                  ({Math.min(LOAD_MORE_SIZE, totalFilesCount - files.length)})
                </span>
              </>
            )}
          </button>

          <button
            onClick={() => fetchCategoryFilesForPortal(selectedCategory, false, 10000)}
            disabled={isLoadingMore}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all shadow-sm hover:shadow-md active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <span>{t('portal.showAll', '显示全部')}</span>
            <span className="text-xs opacity-70 font-normal ml-1">
              ({totalFilesCount - files.length})
            </span>
          </button>
        </div>
        
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {t('portal.showingCount', `已显示 ${files.length} / ${totalFilesCount} 个文件`)}
        </div>
      </div>
    )}
  </div>
</div>
        </>
      )}
      </div>

      {/* 预览模态框 - 使用共用的 FilePreviewModal 组件 */}
      <FilePreviewModal
        file={previewFile!}
        isOpen={showPreviewModal && !!previewFile}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewFile(null);
        }}
        onDownload={handleDownload}
      />
    </PublicLayout>
  );
};

// 主组件，使用 ToastProvider 包装
const PublicPortalPageNew: React.FC = () => {
  return (
    <ToastProvider>
      <PublicPortalPageContent />
    </ToastProvider>
  );
};

export default PublicPortalPageNew;
